import React, { useState, useRef, useEffect, useMemo } from 'react';
import { Search, X, ChevronDown, User } from 'lucide-react';

/**
 * UserSearchSelect — a searchable dropdown for selecting a user.
 *
 * Props:
 *   users          – array of user objects (Name, Username, Email, Division, Tags, sub, UserId …)
 *   value          – currently selected userId (string)
 *   onChange        – (userId, userObject) => void
 *   placeholder     – input placeholder text
 *   disabled        – boolean
 *   requiredSkills  – string[] — when provided, users matching these skills float to the top
 *   isEmployee      – boolean — if true, picker is locked to the current user
 *   currentUser     – the logged-in user object (from AuthContext)
 *   compact         – boolean — render a smaller variant (for checklists / subtask rows)
 */
const UserSearchSelect = ({
    users = [],
    value = '',
    onChange,
    placeholder = 'Search by name or email…',
    disabled = false,
    requiredSkills = [],
    isEmployee = false,
    currentUser = null,
    compact = false,
}) => {
    const [query, setQuery] = useState('');
    const [isOpen, setIsOpen] = useState(false);
    const containerRef = useRef(null);
    const inputRef = useRef(null);

    // ---- helpers ----
    const getId = (u) => u?.sub || u?.UserId || u?.Username || '';
    const getName = (u) => u?.Name || u?.name || u?.Username || u?.username || '';
    const getEmail = (u) => u?.Email || u?.email || u?.Attributes?.find(a => a.Name === 'email')?.Value || '';
    const getDivision = (u) => u?.Division || u?.Attributes?.find(a => a.Name === 'custom:division')?.Value || '';

    // ---- filter out DG / restricted users ----
    const assignableUsers = useMemo(() => {
        return users.filter(u => {
            // Only hide the actual Director General, not other DG group members
            const profile = (u.Profile || u.profile || u.Attributes?.find(a => a.Name === 'profile' || a.Name === 'custom:profile')?.Value || '').toLowerCase();
            const isDirectorGeneral = profile.includes('director general');
            if (isDirectorGeneral) return false;

            // Enforce Coordinator group restrictions
            const currentUserRoles = currentUser?.roles || [];
            const isDGView = currentUserRoles.some(r => r === 'DG' || r === 'DG_OFFICE');
            const coordRole = currentUserRoles.find(r => r.startsWith('COORDINATOR_'));

            if (!isDGView && coordRole) {
                const roleToDivisionMap = {
                    'COORDINATOR_CONNECTIVITY': ['connectivity'],
                    'COORDINATOR_SDG': ['sdg'],
                    'COORDINATOR_FINANCE': ['finance'],
                    'COORDINATOR_AIC': ['aic'],
                    'COORDINATOR_SCIENCE': ['science diplomacy', 'science'],
                    'COORDINATOR_DC': ['development cooperation', 'dc'],
                    'COORDINATOR_BE': ['blue economy', 'be'],
                    'COORDINATOR_FITM': ['fitm'],
                    'COORDINATOR_CMEC': ['cmec'],
                    'COORDINATOR_AGRI': ['agriculture', 'agri'],
                    'COORDINATOR_STD': ['standard', 'std'],
                    'COORDINATOR_DAKSHIN': ['dakshin'],
                    'COORDINATOR_TIWTO': ['trade investment wto', 'tiwto'],
                    'COORDINATOR_RIS': ['central ris', 'ris'],
                    'COORDINATOR_ADMIN': ['dg office', 'admin'],
                    'COORDINATOR_PUB': ['publication and media', 'pub'],
                    'COORDINATOR_SM': ['social media', 'sm'],
                    'COORDINATOR_IT': ['it']
                };

                const currentDivisions = roleToDivisionMap[coordRole] || [];
                const targetDivision = (u.Division || u.Attributes?.find(a => a.Name === 'custom:division')?.Value || '').toLowerCase().trim();

                if (currentDivisions.length > 0 && !currentDivisions.some(div => targetDivision.includes(div))) {
                    u._isExternal = true;
                } else {
                    u._isExternal = false;
                }
            } else {
                u._isExternal = false;
            }
            return true;
        });
    }, [users, currentUser]);

    // ---- search + skill-sort ----
    const filteredUsers = useMemo(() => {
        const q = query.toLowerCase().trim();
        let list = assignableUsers;

        if (q) {
            list = list.filter(u => {
                const n = getName(u).toLowerCase();
                const e = getEmail(u).toLowerCase();
                const d = getDivision(u).toLowerCase();
                return n.includes(q) || e.includes(q) || d.includes(q);
            });
        }

        // skill-based sorting
        if (requiredSkills.length > 0) {
            const scored = list.map(u => {
                const thematicMatches = requiredSkills.filter(s => (u.ThematicSpecialization || []).includes(s));
                const areaMatches = requiredSkills.filter(s => (u.AreaSpecialization || []).includes(s));
                const specialMatches = requiredSkills.filter(s => (u.SpecialSkills || []).includes(s));

                const matchCount = thematicMatches.length + areaMatches.length + specialMatches.length;
                let matchType = '';

                if (matchCount === 1) {
                    if (thematicMatches.length === 1) matchType = 'Thematic';
                    else if (areaMatches.length === 1) matchType = 'Area';
                    else if (specialMatches.length === 1) matchType = 'Special';
                }

                return { ...u, _matchCount: matchCount, _matchType: matchType };
            });
            scored.sort((a, b) => {
                if (b._matchCount !== a._matchCount) return b._matchCount - a._matchCount;

                const currentUserRoles = currentUser?.roles || [];
                const isDGView = currentUserRoles.some(r => r === 'DG' || r === 'DG_OFFICE');
                if (isDGView) {
                    const getIsCoord = (x) => {
                        if (x.Groups && x.Groups.some(g => g.startsWith('COORDINATOR_'))) return 1;
                        if (x.Role?.toLowerCase().includes('coordinator')) return 1;
                        if (x.Profile?.toLowerCase().includes('coordinator')) return 1;
                        return 0;
                    };
                    const aCoord = getIsCoord(a);
                    const bCoord = getIsCoord(b);
                    if (aCoord !== bCoord) return bCoord - aCoord;
                }

                return getName(a).localeCompare(getName(b));
            });
            return scored;
        }

        return [...list].sort((a, b) => {
            const currentUserRoles = currentUser?.roles || [];
            const isDGView = currentUserRoles.some(r => r === 'DG' || r === 'DG_OFFICE');
            if (isDGView) {
                const getIsCoord = (x) => {
                    if (x.Groups && x.Groups.some(g => g.startsWith('COORDINATOR_'))) return 1;
                    if (x.Role?.toLowerCase().includes('coordinator')) return 1;
                    if (x.Profile?.toLowerCase().includes('coordinator')) return 1;
                    return 0;
                };
                const aCoord = getIsCoord(a);
                const bCoord = getIsCoord(b);
                if (aCoord !== bCoord) return bCoord - aCoord;
            }

            if (a._isExternal !== b._isExternal) {
                return a._isExternal ? 1 : -1;
            }

            return getName(a).localeCompare(getName(b));
        });
    }, [assignableUsers, query, requiredSkills, currentUser]);

    // ---- selected user display ----
    const selectedUser = useMemo(() => {
        if (!value) return null;
        return users.find(u => getId(u) === value) || null;
    }, [users, value]);

    // ---- click outside ----
    useEffect(() => {
        const handler = (e) => {
            if (containerRef.current && !containerRef.current.contains(e.target)) {
                setIsOpen(false);
                setQuery('');
            }
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, []);

    const handleSelect = (u) => {
        const id = getId(u);
        onChange?.(id, u);
        setIsOpen(false);
        setQuery('');
    };

    const handleClear = (e) => {
        e.stopPropagation();
        onChange?.('', null);
        setQuery('');
    };

    // ---- employee locked ----
    if (isEmployee && currentUser) {
        const displayName = currentUser?.attributes?.name || currentUser?.Name || currentUser?.name || currentUser?.username || 'Me';
        return (
            <div style={{
                padding: compact ? '0.4rem 0.6rem' : '0.7rem',
                borderRadius: compact ? '6px' : '8px',
                border: '1px solid #cbd5e1',
                background: '#f1f5f9',
                fontSize: compact ? '0.85rem' : '0.95rem',
                color: '#64748b',
                cursor: 'not-allowed',
            }}>
                {displayName} (Me)
            </div>
        );
    }

    if (disabled) {
        return (
            <div style={{
                padding: compact ? '0.4rem 0.6rem' : '0.7rem',
                borderRadius: compact ? '6px' : '8px',
                border: '1px solid #cbd5e1',
                background: '#f1f5f9',
                fontSize: compact ? '0.85rem' : '0.95rem',
                color: '#94a3b8',
            }}>
                {selectedUser ? getName(selectedUser) : placeholder}
            </div>
        );
    }

    return (
        <div ref={containerRef} style={{ position: 'relative', width: '100%' }}>
            {/* Input trigger */}
            <div
                onClick={() => { setIsOpen(true); setTimeout(() => inputRef.current?.focus(), 0); }}
                style={{
                    display: 'flex', alignItems: 'center', gap: '0.5rem',
                    padding: compact ? '0.4rem 0.6rem' : '0.7rem',
                    borderRadius: compact ? '6px' : '8px',
                    border: `1px solid ${isOpen ? '#93c5fd' : '#cbd5e1'}`,
                    background: 'white',
                    cursor: 'text',
                    transition: 'border-color 0.2s, box-shadow 0.2s',
                    boxShadow: isOpen ? '0 0 0 3px rgba(59,130,246,0.15)' : 'none',
                    fontSize: compact ? '0.85rem' : '0.95rem',
                }}
            >
                <Search size={compact ? 14 : 16} color="#94a3b8" style={{ flexShrink: 0 }} />
                {isOpen ? (
                    <input
                        ref={inputRef}
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        placeholder={placeholder}
                        style={{
                            border: 'none', outline: 'none', flex: 1, fontSize: compact ? '0.85rem' : '0.95rem',
                            background: 'transparent', color: '#1e293b',
                        }}
                        onKeyDown={(e) => {
                            if (e.key === 'Escape') { setIsOpen(false); setQuery(''); }
                            if (e.key === 'Enter' && filteredUsers.length > 0) {
                                e.preventDefault();
                                handleSelect(filteredUsers[0]);
                            }
                        }}
                    />
                ) : (
                    <span style={{ flex: 1, color: selectedUser ? '#1e293b' : '#94a3b8', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {selectedUser ? `${getName(selectedUser)} (${getDivision(selectedUser) || 'N/A'})` : placeholder}
                    </span>
                )}
                {selectedUser && !isOpen && (
                    <button type="button" onClick={handleClear} style={{
                        border: 'none', background: 'none', cursor: 'pointer', padding: 0,
                        display: 'flex', alignItems: 'center', color: '#94a3b8',
                    }}>
                        <X size={compact ? 14 : 16} />
                    </button>
                )}
                {!selectedUser && !isOpen && <ChevronDown size={compact ? 14 : 16} color="#94a3b8" style={{ flexShrink: 0 }} />}
            </div>

            {/* Dropdown */}
            {isOpen && (
                <div style={{
                    position: 'absolute', top: '100%', left: 0, right: 0,
                    marginTop: '4px', zIndex: 9999,
                    background: 'white', borderRadius: '10px',
                    border: '1px solid #e2e8f0',
                    boxShadow: '0 10px 25px rgba(0,0,0,0.12)',
                    maxHeight: '260px', overflowY: 'auto',
                }}>
                    {filteredUsers.length === 0 ? (
                        <div style={{ padding: '1rem', textAlign: 'center', color: '#94a3b8', fontSize: '0.85rem' }}>
                            No users found
                        </div>
                    ) : (
                        filteredUsers.map((u, idx) => {
                            const id = getId(u);
                            const isSelected = id === value;
                            const matchCount = u._matchCount || 0;
                            const hasSkillSection = requiredSkills.length > 0;
                            // Separator between recommended and others
                            const showSeparator = hasSkillSection && idx > 0 && filteredUsers[idx - 1]._matchCount > 0 && matchCount === 0;
                            const showExternalSeparator = u._isExternal && (idx === 0 || !filteredUsers[idx - 1]._isExternal);

                            return (
                                <React.Fragment key={id}>
                                    {showExternalSeparator && (
                                        <div style={{ padding: '0.25rem 0.75rem', fontSize: '0.7rem', color: '#b45309', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', background: '#fef3c7', borderTop: '1px solid #fde68a', borderBottom: '1px solid #fde68a' }}>
                                            ⚠️ External Team (Requires Approval)
                                        </div>
                                    )}
                                    {showSeparator && (
                                        <div style={{ padding: '0.25rem 0.75rem', fontSize: '0.7rem', color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', background: '#f8fafc', borderTop: '1px solid #e2e8f0', borderBottom: '1px solid #e2e8f0' }}>
                                            All Users
                                        </div>
                                    )}
                                    {hasSkillSection && idx === 0 && matchCount > 0 && (
                                        <div style={{ padding: '0.25rem 0.75rem', fontSize: '0.7rem', color: '#059669', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', background: '#ecfdf5' }}>
                                            ✅ Recommended
                                        </div>
                                    )}
                                    <div
                                        onClick={() => handleSelect(u)}
                                        style={{
                                            padding: compact ? '0.45rem 0.75rem' : '0.6rem 0.75rem',
                                            cursor: 'pointer',
                                            display: 'flex', alignItems: 'center', gap: '0.6rem',
                                            background: isSelected ? '#eff6ff' : 'transparent',
                                            borderLeft: isSelected ? '3px solid #3b82f6' : '3px solid transparent',
                                            transition: 'background 0.15s',
                                        }}
                                        onMouseEnter={(e) => { if (!isSelected) e.currentTarget.style.background = '#f8fafc'; }}
                                        onMouseLeave={(e) => { if (!isSelected) e.currentTarget.style.background = 'transparent'; }}
                                    >
                                        <div style={{
                                            width: compact ? '28px' : '32px', height: compact ? '28px' : '32px',
                                            borderRadius: '50%', background: 'linear-gradient(135deg, #dbeafe 0%, #e0e7ff 100%)',
                                            display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                                        }}>
                                            <User size={compact ? 14 : 16} color="#6366f1" />
                                        </div>
                                        <div style={{ flex: 1, minWidth: 0 }}>
                                            <div style={{ fontSize: compact ? '0.8rem' : '0.9rem', fontWeight: 600, color: '#1e293b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                {getName(u)}
                                                {matchCount > 0 && (
                                                    <span style={{
                                                        marginLeft: '0.4rem', fontSize: '0.7rem', fontWeight: 600,
                                                        background: '#dcfce7', color: '#166534',
                                                        padding: '1px 6px', borderRadius: '8px',
                                                    }}>
                                                        {matchCount === 1 && u._matchType ? `1 ${u._matchType} match` : `${matchCount} skill match${matchCount > 1 ? 'es' : ''}`}
                                                    </span>
                                                )}
                                            </div>
                                            <div style={{ fontSize: compact ? '0.7rem' : '0.75rem', color: '#64748b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                {getEmail(u) && <span>{getEmail(u)} · </span>}
                                                {getDivision(u) || 'N/A'}
                                            </div>
                                        </div>
                                    </div>
                                </React.Fragment>
                            );
                        })
                    )}
                </div>
            )}
        </div>
    );
};

export default UserSearchSelect;
